import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import * as crmBackend from '../services/crmBackendService';
import { SignaturePad } from '../components/SignaturePad';

// "Request Customer Approval" — a technician captures the customer's signature
// in person (on site) confirming the job/items as they stand. This writes to the
// SAME custom_signature_image / custom_signature_captured fields the customer
// portal and print formats already read — it is not a separate/parallel signing
// system, just a second way to capture the same real approval evidence.
const TechApproval = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [alreadyCaptured, setAlreadyCaptured] = useState(false);

  useEffect(() => {
    (async () => {
      const result = await crmBackend.getQuotationDetail(jobId);
      if (result.success && result.data) {
        const q = result.data;
        setJob({
          name: q.name,
          customer_name: q.party_name || q.customer_name || '',
          grand_total: q.grand_total,
          items: q.items || [],
        });
        setCustomerName(q.party_name || q.customer_name || '');
        setAlreadyCaptured(q.custom_signature_captured === 1 || q.docstatus === 1);
      } else {
        toast({ title: 'Could not load job', description: result.error, variant: 'destructive' });
      }
      setLoading(false);
    })();
  }, [jobId, toast]);

  const handleSubmit = async () => {
    if (!signatureDataUrl) {
      toast({ title: 'Signature required', description: 'Have the customer sign above before submitting.', variant: 'destructive' });
      return;
    }
    if (!customerName.trim()) {
      toast({ title: 'Customer name required', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await crmBackend.submitEstimateSignature(jobId, {
        signatureDataUrl,
        customerName: customerName.trim(),
      });
      toast({ title: 'Approval captured', description: 'Customer signature saved to the job.' });
      navigate(`/tech/job/${jobId}`);
    } catch (err) {
      toast({
        title: 'Could not save signature',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-[calc(6rem+env(safe-area-inset-bottom,0px))]">
      <div className="bg-blue-600 text-white sticky top-0 z-50 shadow-lg">
        <div className="px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate(`/tech/job/${jobId}`)}
            className="flex items-center space-x-2 hover:bg-blue-700 px-3 py-2 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back</span>
          </button>
          <span className="font-bold">Customer Approval</span>
          <div className="w-16" />
        </div>
      </div>

      <div className="p-4 space-y-4">
        {alreadyCaptured && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-800 text-sm font-semibold flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 shrink-0" />
            A signature has already been captured for this job. Signing again will replace it.
          </div>
        )}

        <Card>
          <CardHeader><CardTitle className="text-base">Job Summary</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p className="text-gray-500">Job <span className="font-medium text-gray-900">{job?.name}</span></p>
            {(job?.items || []).map((it, idx) => (
              <div key={idx} className="flex justify-between text-gray-700">
                <span>{it.item_name}</span>
                <span>${Number(it.amount || 0).toFixed(2)}</span>
              </div>
            ))}
            <div className="flex justify-between font-bold text-gray-900 pt-2 border-t">
              <span>Total</span>
              <span>${Number(job?.grand_total || 0).toFixed(2)}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Customer Name</CardTitle></CardHeader>
          <CardContent>
            <Input
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Customer signing"
              className="h-11"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Signature</CardTitle></CardHeader>
          <CardContent>
            <SignaturePad onChange={setSignatureDataUrl} height={200} />
          </CardContent>
        </Card>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))]">
        <Button
          onClick={handleSubmit}
          disabled={submitting || !signatureDataUrl}
          className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
        >
          {submitting ? 'Saving…' : 'Submit Approval'}
        </Button>
      </div>
    </div>
  );
};

export default TechApproval;
